export type Rotation = 0 | 90 | 180 | 270;

export type CropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export const FULL_CROP: CropRect = { x: 0, y: 0, width: 1, height: 1 };

type Rgb = { r: number; g: number; b: number };

export const SCAN_LIMITS = {
  maxPages: 20,
  maxFileBytes: 25 * 1024 * 1024,
  maxTotalBytes: 120 * 1024 * 1024,
  maxMegapixels: 40,
  maxOutputSide: 2600,
} as const;

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export function normalizeCrop(crop: CropRect, minimumSize = 0.1): CropRect {
  const width = clamp(crop.width, minimumSize, 1);
  const height = clamp(crop.height, minimumSize, 1);
  return {
    x: clamp(crop.x, 0, 1 - width),
    y: clamp(crop.y, 0, 1 - height),
    width,
    height,
  };
}

function median(values: number[]): number {
  if (!values.length) return 0;
  values.sort((left, right) => left - right);
  const middle = Math.floor(values.length / 2);
  return values.length % 2 ? values[middle] : (values[middle - 1] + values[middle]) / 2;
}

function percentile(values: number[], fraction: number): number {
  if (!values.length) return 0;
  values.sort((left, right) => left - right);
  return values[Math.min(values.length - 1, Math.max(0, Math.floor((values.length - 1) * fraction)))];
}

function colorDistance(data: Uint8ClampedArray, offset: number, background: Rgb): number {
  return (
    Math.abs(data[offset] - background.r)
    + Math.abs(data[offset + 1] - background.g)
    + Math.abs(data[offset + 2] - background.b)
  ) / 3;
}

/**
 * Finds a conservative, axis-aligned document boundary in a small RGBA preview.
 * It returns null when the page does not contrast clearly with its surroundings,
 * which keeps the original image as the safe fallback for this beta feature.
 */
export function detectDocumentCrop(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): CropRect | null {
  if (width < 24 || height < 24 || data.length < width * height * 4) return null;

  const borderDepth = Math.max(1, Math.round(Math.min(width, height) * 0.035));
  const red: number[] = [];
  const green: number[] = [];
  const blue: number[] = [];
  const borderOffsets: number[] = [];
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (x >= borderDepth && x < width - borderDepth && y >= borderDepth && y < height - borderDepth) continue;
      const offset = (y * width + x) * 4;
      red.push(data[offset]);
      green.push(data[offset + 1]);
      blue.push(data[offset + 2]);
      borderOffsets.push(offset);
    }
  }
  const background = { r: median(red), g: median(green), b: median(blue) };
  const borderDistances = borderOffsets.map((offset) => colorDistance(data, offset, background));
  const threshold = Math.max(11, percentile(borderDistances, 0.9) + 7);

  const foreground = new Uint8Array(width * height);
  let foregroundCount = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const index = y * width + x;
      if (colorDistance(data, index * 4, background) >= threshold) {
        foreground[index] = 1;
        foregroundCount += 1;
      }
    }
  }
  if (foregroundCount < width * height * 0.025) return null;

  // Bridge small gaps from print, glare, and shadows before choosing the largest region.
  const bridged = foreground.slice();
  for (let y = 2; y < height - 2; y += 1) {
    for (let x = 2; x < width - 2; x += 1) {
      const index = y * width + x;
      if (foreground[index]) continue;
      const horizontal = foreground[index - 2] && foreground[index + 2];
      const vertical = foreground[index - width * 2] && foreground[index + width * 2];
      if (horizontal || vertical) bridged[index] = 1;
    }
  }

  const seen = new Uint8Array(width * height);
  const queue = new Int32Array(width * height);
  let best: { count: number; minX: number; minY: number; maxX: number; maxY: number } | null = null;
  for (let start = 0; start < bridged.length; start += 1) {
    if (!bridged[start] || seen[start]) continue;
    let head = 0;
    let tail = 0;
    queue[tail++] = start;
    seen[start] = 1;
    let count = 0;
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    while (head < tail) {
      const index = queue[head++];
      const x = index % width;
      const y = Math.floor(index / width);
      count += 1;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
      const neighbors = [index - 1, index + 1, index - width, index + width];
      for (const neighbor of neighbors) {
        if (neighbor < 0 || neighbor >= bridged.length || seen[neighbor] || !bridged[neighbor]) continue;
        const neighborX = neighbor % width;
        if (Math.abs(neighborX - x) > 1) continue;
        seen[neighbor] = 1;
        queue[tail++] = neighbor;
      }
    }
    if (!best || count > best.count) best = { count, minX, minY, maxX, maxY };
  }
  if (!best || best.count < width * height * 0.02) return null;

  const componentWidth = best.maxX - best.minX + 1;
  const componentHeight = best.maxY - best.minY + 1;
  if (componentWidth < width * 0.22 || componentHeight < height * 0.22) return null;

  const padding = Math.max(2, Math.round(Math.min(width, height) * 0.012));
  const left = Math.max(0, best.minX - padding);
  const top = Math.max(0, best.minY - padding);
  const right = Math.min(width, best.maxX + 1 + padding);
  const bottom = Math.min(height, best.maxY + 1 + padding);
  const crop = normalizeCrop({
    x: left / width,
    y: top / height,
    width: (right - left) / width,
    height: (bottom - top) / height,
  });
  if (crop.width * crop.height > 0.985) return null;
  return crop;
}

export function nextRotation(rotation: Rotation): Rotation {
  return ((rotation + 90) % 360) as Rotation;
}

export function orientedSize(width: number, height: number, rotation: Rotation) {
  return rotation === 90 || rotation === 270
    ? { width: height, height: width }
    : { width, height };
}

export function outputSize(
  width: number,
  height: number,
  rotation: Rotation,
  crop: CropRect,
  maxSide: number = SCAN_LIMITS.maxOutputSide,
) {
  const oriented = orientedSize(width, height, rotation);
  const safeCrop = normalizeCrop(crop);
  const cropWidth = Math.max(1, Math.round(oriented.width * safeCrop.width));
  const cropHeight = Math.max(1, Math.round(oriented.height * safeCrop.height));
  const scale = Math.min(1, maxSide / Math.max(cropWidth, cropHeight));
  return {
    width: Math.max(1, Math.round(cropWidth * scale)),
    height: Math.max(1, Math.round(cropHeight * scale)),
    cropWidth,
    cropHeight,
    scale,
  };
}

export function sanitizeBaseName(value: string): string {
  const withoutControlCharacters = Array.from(value)
    .filter((character) => character.charCodeAt(0) >= 32)
    .join('');
  const normalized = withoutControlCharacters
    .trim()
    .normalize('NFKC')
    .replace(/\.(pdf|jpe?g)$/i, '')
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/g, '')
    .trim()
    .slice(0, 90);
  return normalized || 'my-scan';
}

export function numberedJpegName(baseName: string, pageIndex: number, pageCount: number): string {
  const safe = sanitizeBaseName(baseName);
  if (pageCount <= 1) return `${safe}.jpg`;
  const digits = Math.max(2, String(pageCount).length);
  return `${safe}-${String(pageIndex + 1).padStart(digits, '0')}.jpg`;
}

export function validateImageFile(
  file: Pick<File, 'name' | 'type' | 'size'>,
  currentPages: number,
  currentBytes: number,
): string | null {
  if (currentPages >= SCAN_LIMITS.maxPages) {
    return `You can scan up to ${SCAN_LIMITS.maxPages} pages at a time.`;
  }
  const allowedType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type.toLowerCase());
  const allowedName = /\.(jpe?g|png|webp)$/i.test(file.name);
  if (!allowedType && !allowedName) return `${file.name}: choose a JPG, PNG, or WebP image.`;
  if (!file.size) return `${file.name}: this image is empty.`;
  if (file.size > SCAN_LIMITS.maxFileBytes) return `${file.name}: this image is over 25 MB.`;
  if (currentBytes + file.size > SCAN_LIMITS.maxTotalBytes) {
    return 'Those pages are over the 120 MB scan limit.';
  }
  return null;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
